import { useState, useRef, useEffect, type FC } from 'react';
import { LuCalendar, LuChevronDown, LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import './MonthPicker.css';

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const SHORT_MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

interface MonthPickerProps {
    value: string; // YYYY-MM
    onChange: (value: string) => void;
}

/**
 * Seletor de mês compacto do cabeçalho de tela: um botão com o mês corrente
 * que abre a grade num popover. Era uma faixa com os doze meses à mostra, que
 * tomava a largura inteira do header.
 */
const MonthPicker: FC<MonthPickerProps> = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const [yearStr, monthStr] = value.split('-');
    const selectedYear = parseInt(yearStr);
    const selectedMonth = parseInt(monthStr) - 1; // 0-indexed

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const setYear = (delta: number) => onChange(`${selectedYear + delta}-${monthStr}`);

    const setMonth = (index: number) => {
        onChange(`${selectedYear}-${String(index + 1).padStart(2, '0')}`);
        setIsOpen(false);
    };

    return (
        <div className="month-picker" ref={containerRef}>
            <button
                type="button"
                className="month-picker-trigger"
                onClick={() => setIsOpen(v => !v)}
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                <LuCalendar />
                <span>{MONTHS[selectedMonth]} {selectedYear}</span>
                <LuChevronDown className="month-picker-caret" />
            </button>

            {isOpen && (
                <div className="month-picker-popover">
                    <div className="month-picker-year">
                        <button type="button" className="year-nav-btn" onClick={() => setYear(-1)} aria-label="Ano anterior">
                            <LuChevronLeft />
                        </button>
                        <span className="year-label">{selectedYear}</span>
                        <button type="button" className="year-nav-btn" onClick={() => setYear(1)} aria-label="Próximo ano">
                            <LuChevronRight />
                        </button>
                    </div>

                    <div className="month-picker-grid">
                        {SHORT_MONTHS.map((m, i) => (
                            <button
                                key={i}
                                type="button"
                                className={`month-cell ${i === selectedMonth ? 'is-active' : ''}`}
                                onClick={() => setMonth(i)}
                            >
                                {m}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MonthPicker;
